import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";

const mapContainerStyle = {
  width: "100%",
  height: "600px",
};

const getMarkerColor = (device) => {
  if (device.status === "outage") return "red";
  if (device.status === "offline") return "blue";
  const v1 = device.voltageL1 || 120;
  const v2 = device.voltageL2 || 120;
  const low = 108;
  const high = 132;
  if (v1 < low || v1 > high || v2 < low || v2 > high) return "yellow";
  if (device.blinkTracker > 0) return "orange";
  return "green";
};

const FullMap = () => {
  const [devices, setDevices] = useState([]);
  const [geocodedDevices, setGeocodedDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const searchInputRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  useEffect(() => {
    fetch("/api/devices")
      .then((res) => res.json())
      .then((data) => {
        setDevices(data);
        if (isLoaded) geocodeMissingDevices(data);
      })
      .catch((err) => console.error("Failed to load devices", err));
  }, [isLoaded]);

  const geocodeMissingDevices = (deviceList) => {
    if (!window.google?.maps?.Geocoder) return;
    const geocoder = new window.google.maps.Geocoder();

    deviceList.forEach((device) => {
      if (!device.lat && device.address) {
        geocoder.geocode({ address: device.address }, (results, status) => {
          if (status === "OK" && results[0]) {
            const { lat, lng } = results[0].geometry.location;
            const enriched = { ...device, lat: lat(), lon: lng() };
            setGeocodedDevices((prev) => [...prev, enriched]);
          }
        });
      }
    });
  };

  const allDevices = [
    ...devices.filter((d) => d.lat && d.lon),
    ...geocodedDevices,
  ];

  const getMapCenter = () => {
    if (allDevices.length === 0) return { lat: 39.8, lng: -88.4 };
    const avgLat = allDevices.reduce((sum, d) => sum + d.lat, 0) / allDevices.length;
    const avgLng = allDevices.reduce((sum, d) => sum + d.lon, 0) / allDevices.length;
    return { lat: avgLat, lng: avgLng };
  };

  const handleMapLoad = (map) => {
    mapRef.current = map;
    if (window.google?.maps?.places && searchInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current);
      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (place.geometry?.location) {
          map.panTo(place.geometry.location);
          map.setZoom(14);
        }
      });
    }
  };

  if (!isLoaded) {
    return <div style={{ padding: "2rem" }}>Loading Google Maps...</div>;
  }

  return (
    <div style={{ padding: "2rem", color: "#444" }}>
      <button
        onClick={() => navigate("/dashboard")}
        style={{
          background: "#1976d2",
          color: "#fff",
          border: "none",
          padding: "0.5rem 1rem",
          borderRadius: "5px",
          cursor: "pointer",
          marginBottom: "1rem",
        }}
      >
        ← Return to Dashboard
      </button>

      <h2>📍 Device Map</h2>

      <input
        type="text"
        ref={searchInputRef}
        placeholder="Search address..."
        style={{
          width: "300px",
          padding: "8px",
          fontSize: "14px",
          marginBottom: "1rem",
          borderRadius: "4px",
          border: "1px solid #ccc",
        }}
      />

      <div
        style={{
          background: "#fff",
          padding: "1rem",
          borderRadius: "8px",
          marginTop: "1rem",
        }}
      >
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={getMapCenter()}
          zoom={9}
          onLoad={handleMapLoad}
        >
          {allDevices.map((device) => (
            <Marker
              key={device.deviceId}
              position={{ lat: device.lat, lng: device.lon }}
              icon={{
                url: `http://maps.google.com/mapfiles/ms/icons/${getMarkerColor(device)}-dot.png`,
                scaledSize: new window.google.maps.Size(40, 40),
              }}
              onClick={() => setSelectedDevice(device)}
            />
          ))}

          {selectedDevice && (
            <InfoWindow
              position={{ lat: selectedDevice.lat, lng: selectedDevice.lon }}
              onCloseClick={() => setSelectedDevice(null)}
            >
              <div style={{ fontSize: "14px", lineHeight: "1.5" }}>
                <strong>Device ID:</strong> {selectedDevice.deviceId}<br />
                <strong>Meter:</strong> {selectedDevice.meter || selectedDevice.meterNumber || "—"}<br />
                <strong>Account:</strong> {selectedDevice.account || selectedDevice.accountNumber || "—"}<br />
                <strong>Status:</strong> {selectedDevice.status}<br />
                <strong>Phase:</strong> {selectedDevice.phase || "Unknown"}<br />
                <strong>Battery:</strong> {selectedDevice.battery || "—"}<br />
                <strong>Voltage L1:</strong> {selectedDevice.voltageL1 || "—"}<br />
                <strong>Voltage L2:</strong> {selectedDevice.voltageL2 || "—"}<br />
                <strong>Last Seen:</strong><br />
                {selectedDevice.lastSeen
                  ? new Date(selectedDevice.lastSeen).toLocaleString()
                  : "—"}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>
    </div>
  );
};

export default FullMap;
